'use client';

import * as SubframeCore from '@subframe/core';
import { FeatherActivity, FeatherClock, FeatherFileText } from '@subframe/core';
import React from 'react';
import { Badge, Button } from '@/shared/components/ui';
import * as SubframeUtils from '../utils';

interface AgentCardCreationProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: React.ReactNode;
  title: string;
  description: string;
  successRate?: string;
  avgTime?: string;
  stat1Label?: string;
  stat2Label?: string;
  startLabel?: string;
  detailsLabel?: string;
  includesContract?: boolean;
  isPremium?: boolean;
  statusBadge?: React.ReactNode;
  onStart?: () => void;
  onSeeDetails?: () => void;
  className?: string;
}

export const AgentCardCreation = React.forwardRef<HTMLDivElement, AgentCardCreationProps>(
  function AgentCardCreation(
    {
      icon,
      title,
      description,
      successRate,
      avgTime,
      stat1Label = 'Taxa de Sucesso',
      stat2Label = 'Tempo Médio',
      startLabel = 'Começar',
      detailsLabel = 'Ver detalhes',
      includesContract,
      isPremium,
      statusBadge,
      onStart,
      onSeeDetails,
      className,
      ...otherProps
    },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={SubframeUtils.twClassNames(
          'group/agent-card bg-default-background hover:border-brand-300 flex min-w-[340px] flex-col items-start gap-4 rounded-lg border border-solid border-neutral-200 p-6 shadow-sm transition-all duration-300 hover:shadow-md',
          className,
        )}
        {...otherProps}
      >
        <div className="flex w-full items-start justify-between">
          <div className="bg-brand-50 text-brand-600 flex h-12 w-12 items-center justify-center overflow-hidden rounded-sm">
            <SubframeCore.IconWrapper className="text-2xl">{icon}</SubframeCore.IconWrapper>
          </div>
          <div className="flex gap-2">
            {isPremium && (
              <Badge variant="brand" className="bg-brand-100 text-brand-700 border-none">
                Premium
              </Badge>
            )}
            {statusBadge}
          </div>
        </div>

        <div className="flex flex-col items-start gap-1">
          <span className="text-heading-3 font-heading-3 text-default-font">{title}</span>
          <span className="text-body text-subtext-color line-clamp-2 min-h-[40px]">
            {description}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {includesContract && (
            <Badge icon={<FeatherFileText />} variant="neutral">
              Inclui contrato
            </Badge>
          )}
        </div>

        <div className="mt-2 flex w-full items-center gap-4 border-t border-solid border-neutral-100 pt-4">
          <div className="flex flex-col items-start">
            <span className="text-subtext-color text-[10px] font-medium tracking-wider uppercase">
              {stat1Label}
            </span>
            <div className="flex items-center gap-1">
              <FeatherActivity className="text-success-600 h-3 w-3" />
              <span className="text-body-bold font-body-bold text-default-font">{successRate}</span>
            </div>
          </div>
          <div className="h-8 w-px bg-neutral-100" />
          <div className="flex flex-col items-start">
            <span className="text-subtext-color text-[10px] font-medium tracking-wider uppercase">
              {stat2Label}
            </span>
            <div className="flex items-center gap-1">
              <FeatherClock className="text-brand-600 h-3 w-3" />
              <span className="text-body-bold font-body-bold text-default-font">{avgTime}</span>
            </div>
          </div>
        </div>

        <div className="mt-2 flex w-full justify-end gap-3">
          {onSeeDetails && (
            <Button
              className="w-auto"
              variant="neutral-secondary"
              size="small"
              onClick={onSeeDetails}
            >
              {detailsLabel}
            </Button>
          )}
          {onStart && (
            <Button className="w-auto" variant="brand-primary" size="small" onClick={onStart}>
              {startLabel}
            </Button>
          )}
        </div>
      </div>
    );
  },
);
